import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Container, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { BasicDialog, LogoBack, OneTimeCodeTextField } from 'components';
import { AUTH_PATHS } from 'constants/paths';
import { UserDeletionStatus } from 'constants/users';
import { useIsMountedRef, useUsers } from 'hooks';
import { FormProvider } from 'providers';
import { useAuthSelector, useAuthStore, useGlobalStore } from 'stores';

const Mfa = () => {
  // hooks
  const isMountedRef = useIsMountedRef();
  const navigate = useNavigate();
  const { handleConfirmTOTPSignIn, getSession } = useAuthSelector();
  const challengedUser = useAuthStore.getState().challengedUser;
  const { getUserInfo, getUserDeletionInfo, userCancelDelete } = useUsers();

  // form
  const methods = useForm({
    defaultValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    },
  });

  const {
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting, errors },
    watch,
  } = methods;

  // state
  const [openDeletionModal, setOpenDeletionModal] = useState(false);
  const [pendingCode, setPendingCode] = useState();
  const isValidForm =
    watch('code1') &&
    watch('code2') &&
    watch('code3') &&
    watch('code4') &&
    watch('code5') &&
    watch('code6');

  // functions
  const getUserDeletionStatus = async () => {
    const userDeletionStatus = await getUserDeletionInfo();
    return userDeletionStatus?.deletionStatus;
  };

  const completeLoginProcess = async () => {
    try {
      const user = await getUserInfo();
      useAuthStore.setState({ user });
    } catch (err) {
      console.log(err);
    }
    await getSession();
    useGlobalStore.setState({ requestedLocation: null });
  };

  const onSubmit = async data => {
    const code = Object.entries(data)
      .filter(([key]) => key.startsWith('code'))
      .map(([, value]) => value)
      .join('');

    try {
      await handleConfirmTOTPSignIn(challengedUser, code);
      setPendingCode(code);
      const deletionStatus = await getUserDeletionStatus();
      if (deletionStatus === UserDeletionStatus.InDeletion) {
        setOpenDeletionModal(true);
      } else {
        await completeLoginProcess();
      }
    } catch (error) {
      console.error(error);

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    } finally {
      reset();
    }
  };

  const handleConfirmCancelDelete = async () => {
    try {
      await userCancelDelete();
      if (pendingCode) {
        await completeLoginProcess();
      }
      navigate(AUTH_PATHS.LOGIN);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <LogoBack path={AUTH_PATHS.LOGIN} />

      <Container>
        <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="h2" sx={{ fontSize: 18 }} paragraph>
            {'Autenticazione a 2 fattori'}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {"Inserisci il codice generato dall'app di autenticazione"}
          </Typography>

          <Box sx={{ mt: 5 }}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              {!!errors.afterSubmit && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">{errors.afterSubmit.message}</Alert>
                </Box>
              )}

              <OneTimeCodeTextField fieldsPrefix="code" />
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={!isValidForm}
                sx={{ mt: 5 }}
              >
                {'Conferma'}
              </LoadingButton>
            </FormProvider>
          </Box>
        </Box>
      </Container>

      <BasicDialog
        title="Attenzione"
        open={openDeletionModal}
        onClose={() => setOpenDeletionModal(false)}
        actions={
          <>
            <Button
              onClick={() => {
                navigate(AUTH_PATHS.LOGIN);
              }}
            >
              Annulla
            </Button>
            <Button variant="contained" onClick={handleConfirmCancelDelete}>
              Conferma
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {`Attenzione! Per il tuo account è stata effettuata una richiesta di eliminazione. Procedendo con l’accesso la tua richiesta di eliminazione sarà revocata e qualora lo desideri sarà necessario effettuare nuovamente la procedura di eliminazione. Vuoi davvero procedere con l’accesso?`}
          </Grid>
        </Grid>
      </BasicDialog>
    </>
  );
};

export default Mfa;
